import React, { useState, useEffect } from "react";
import { getBuyerIntel, getIntentBoard, matchForBuyer, matchForProperty, notifyMatchedBuyers, previewBlast, getBlastLog, fetchProperties } from "../../../api";
import { ScoreBadge } from "./bits";
import { Dialog, DialogContent, DialogTitle } from "../../ui/dialog";
import { Users, Loader2, Home, Flame, TrendingUp, Send, Mail, ShieldCheck, History, X, AlertTriangle } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";

const money = (n) => (n || n === 0 ? `£${Number(n).toLocaleString()}` : "—");
const intentColor = { high: "bg-red-100 text-red-700", rising: "bg-amber-100 text-amber-700", low: "bg-gray-100 text-gray-500" };

const AdminBuyerIntelligence = () => {
  const { toast } = useToast();
  const [buyers, setBuyers] = useState([]);
  const [intent, setIntent] = useState({ sellerIntent: [], buyerIntent: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [matches, setMatches] = useState(null);
  // Listing -> buyers ("Send These Buyers")
  const [props, setProps] = useState([]);
  const [selProp, setSelProp] = useState("");
  const [propMatches, setPropMatches] = useState(null);
  const [threshold, setThreshold] = useState(70);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(null);   // preview payload for confirm dialog
  const [previewing, setPreviewing] = useState(false);
  const [log, setLog] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [b, i, p] = await Promise.all([getBuyerIntel(), getIntentBoard(), fetchProperties({ status: "for-sale" })]);
      setBuyers(b.items || []); setIntent(i);
      const list = Array.isArray(p) ? p : (p.items || []);
      setProps(list);
      if (list[0]) { setSelProp(list[0].id); loadPropMatches(list[0].id); loadLog(list[0].id); }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  const loadPropMatches = async (id) => {
    setPropMatches(null);
    try { const m = await matchForProperty(id); setPropMatches(m.items || []); } catch { setPropMatches([]); }
  };
  const loadLog = async (id) => {
    try { const l = await getBlastLog(id); setLog(l.items || []); } catch { setLog([]); }
  };
  const onSelProp = (id) => { setSelProp(id); loadPropMatches(id); loadLog(id); };

  const openConfirm = async () => {
    setPreviewing(true);
    try { const p = await previewBlast(selProp, threshold); setConfirm(p); }
    catch { toast({ title: "Couldn't build preview", variant: "destructive" }); }
    setPreviewing(false);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const r = await notifyMatchedBuyers(selProp, threshold);
      toast({ title: `Emailed ${r.sentCount} matched buyer${r.sentCount === 1 ? "" : "s"}`, description: r.skipped?.length ? `${r.skipped.length} skipped` : undefined });
      setConfirm(null);
      loadPropMatches(selProp); loadLog(selProp);
    } catch { toast({ title: "Couldn't send emails", variant: "destructive" }); }
    setSending(false);
  };

  const openBuyer = async (b) => {
    setOpen(b); setMatches(null);
    try { const m = await matchForBuyer(b.id); setMatches(m.items || []); } catch { setMatches([]); }
  };

  if (loading) return <div className="py-16 grid place-items-center text-[#8a8a8a]"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-buyer-intelligence">
      <div>
        <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><Users size={20} className="text-[#b8935f]" /> Buyer Intelligence</h2>
        <p className="text-[13px] text-[#8a8a8a] mt-1">Inferred buyer profiles, qualification and rising intent. Behavioural affinities are estimates.</p>
      </div>

      {/* Intent board */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#f0ece5] rounded-xl p-4">
          <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-[#b8935f]" /> Seller intent (valuation activity)</h3>
          <div className="space-y-2">
            {intent.sellerIntent.map((s, i) => (
              <div key={i} className="flex items-center gap-3 text-[13px]" data-testid={`seller-intent-${i}`}>
                <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${intentColor[s.level]}`}>{s.level}</span>
                <div className="flex-1 min-w-0"><div className="font-medium text-[#2b2b2b] truncate">{s.name}</div><div className="text-[12px] text-[#8a8a8a] truncate">{s.signal}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#f0ece5] rounded-xl p-4">
          <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><Flame size={16} className="text-[#b8935f]" /> Buyer intent</h3>
          <div className="space-y-2">
            {intent.buyerIntent.length === 0 ? <div className="text-[13px] text-[#8a8a8a]">No high-intent buyers right now.</div> :
              intent.buyerIntent.map((b, i) => (
                <div key={i} className="flex items-center gap-3 text-[13px]"><span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${intentColor[b.intent.level]}`}>{b.intent.level}</span><span className="font-medium text-[#2b2b2b]">{b.name}</span><span className="text-[12px] text-[#8a8a8a]">{b.intent.recentSignals} signals</span></div>
              ))}
          </div>
        </div>
      </div>

      {/* Send These Buyers — listing → matched buyers */}
      <div className="bg-white border border-[#f0ece5] rounded-xl p-4" data-testid="send-these-buyers">
        <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><Send size={16} className="text-[#b8935f]" /> Send a listing to matched buyers</h3>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <select value={selProp} onChange={(e) => onSelProp(e.target.value)} data-testid="send-property-select" className="text-[13px] border border-[#f0ece5] rounded-lg px-3 py-2 min-w-[240px] flex-1">
            {props.map((p) => <option key={p.id} value={p.id}>{p.title}{p.price ? ` — £${Number(p.price).toLocaleString()}` : ""}</option>)}
          </select>
          <label className="text-[12px] text-[#6a6a6a] flex items-center gap-2">Min match
            <input type="number" min="0" max="100" value={threshold} onChange={(e) => setThreshold(+e.target.value)} data-testid="send-threshold" className="w-16 text-[13px] border border-[#f0ece5] rounded-lg px-2 py-1.5" />%
          </label>
          <button onClick={openConfirm} disabled={previewing || !selProp} data-testid="send-buyers-btn" className="flex items-center gap-2 text-[13px] bg-[#b8935f] hover:bg-[#a37f4e] text-white rounded-lg px-4 py-2 disabled:opacity-60">
            {previewing ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Review &amp; email {propMatches ? propMatches.filter((m) => m.match >= threshold).length : 0} buyer(s)
          </button>
        </div>
        {!propMatches ? <div className="py-4 text-center"><Loader2 size={16} className="animate-spin text-[#8a8a8a] inline" /></div> : (
          <div className="space-y-1.5">
            {propMatches.map((m) => (
              <div key={m.buyerId} className={`flex items-center gap-3 text-[13px] rounded-lg px-2 py-1.5 ${m.match >= threshold ? "bg-[#faf7f2]" : "opacity-50"}`} data-testid={`prop-match-${m.buyerId}`}>
                <span className="text-[15px] font-bold text-[#b8935f] w-12">{m.match}%</span>
                <div className="flex-1 min-w-0"><span className="font-medium text-[#2b2b2b]">{m.name}</span> <span className="text-[12px] text-[#8a8a8a]">{m.reasons.slice(0, 2).join(", ")}{m.concern ? ` · ⚠ ${m.concern}` : ""}</span></div>
                {m.match >= threshold && <Mail size={13} className="text-[#b8935f]" />}
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-[#a8a8a8] italic mt-2 flex items-center gap-1.5"><ShieldCheck size={13} /> You'll see a preview and confirm before anything sends. Buyers emailed this listing in the last 14 days are skipped automatically.</p>

        {log.length > 0 && (
          <div className="mt-4 border-t border-[#f5f1ea] pt-3" data-testid="blast-log">
            <div className="text-[12px] font-medium text-[#6a6a6a] mb-2 flex items-center gap-1.5"><History size={14} /> Send history for this listing</div>
            <div className="space-y-1">
              {log.map((b) => (
                <div key={b.id} className="text-[12px] text-[#8a8a8a] flex items-center justify-between" data-testid={`blast-log-${b.id}`}>
                  <span>{new Date(b.sentAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {b.sentBy}</span>
                  <span className="text-[#4a4a4a]">{b.sentCount} sent{b.skipped?.length ? ` · ${b.skipped.length} skipped` : ""} (≥{b.threshold}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Confirm dialog — preview before send */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="blast-confirm-dialog">
          <DialogTitle className="sr-only">Confirm buyer email blast</DialogTitle>
          {confirm && (
            <div className="space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-[#b8935f] flex items-center gap-1.5"><ShieldCheck size={13} /> Confirm before sending</div>
                <h2 className="text-[20px] font-semibold text-[#2b2b2b] mt-1">{confirm.property.title}</h2>
                <p className="text-[13px] text-[#6a6a6a]">This will email <strong>{confirm.recipientCount}</strong> buyer{confirm.recipientCount === 1 ? "" : "s"} matching ≥{confirm.threshold}%.</p>
              </div>
              {confirm.recipientCount === 0 ? (
                <div className="bg-amber-50 text-amber-800 text-[13px] rounded-lg px-3 py-2 flex items-center gap-2"><AlertTriangle size={15} /> No new recipients — everyone matching was already emailed recently or has no email.</div>
              ) : (
                <div>
                  <div className="text-[12px] font-medium text-[#6a6a6a] mb-1">Recipients</div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {confirm.recipients.map((r) => (
                      <div key={r.email} className="flex items-center gap-3 text-[13px] bg-[#faf7f2] rounded-lg px-2 py-1.5" data-testid={`confirm-recipient-${r.email}`}>
                        <span className="text-[14px] font-bold text-[#b8935f] w-11">{r.match}%</span>
                        <div className="flex-1 min-w-0"><span className="font-medium text-[#2b2b2b]">{r.name}</span> <span className="text-[12px] text-[#8a8a8a]">{r.email}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {confirm.skipped?.length > 0 && (
                <div className="text-[12px] text-[#8a8a8a]">Skipped: {confirm.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}</div>
              )}
              {confirm.sampleEmail && (
                <div>
                  <div className="text-[12px] font-medium text-[#6a6a6a] mb-1">Email preview</div>
                  <div className="text-[12px] text-[#8a8a8a] mb-1">Subject: <span className="text-[#2b2b2b]">{confirm.sampleEmail.subject}</span></div>
                  <div className="border border-[#f0ece5] rounded-lg overflow-hidden bg-white max-h-72 overflow-y-auto" data-testid="email-preview" dangerouslySetInnerHTML={{ __html: confirm.sampleEmail.html }} />
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setConfirm(null)} data-testid="blast-cancel" className="text-[13px] px-4 py-2 rounded-lg border border-[#f0ece5] text-[#6a6a6a] hover:bg-[#faf7f2]">Cancel</button>
                <button onClick={confirmSend} disabled={sending || confirm.recipientCount === 0} data-testid="blast-confirm-send" className="flex items-center gap-2 text-[13px] bg-[#b8935f] hover:bg-[#a37f4e] text-white rounded-lg px-4 py-2 disabled:opacity-60">
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Confirm &amp; send {confirm.recipientCount || ""}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Buyers list */}
      <div className="space-y-3">
        {buyers.map((b) => (
          <button key={b.id} onClick={() => openBuyer(b)} data-testid={`buyer-card-${b.id}`} className="w-full text-left bg-white border border-[#f0ece5] rounded-xl p-4 hover:border-[#b8935f] transition-all flex items-start gap-4">
            <ScoreBadge score={b.qualification.score} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#2b2b2b]">{b.name}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${b.qualification.priority === "immediate" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{b.qualification.priority}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${intentColor[b.intent.level]}`}>{b.intent.level} intent</span>
              </div>
              <div className="text-[12px] text-[#8a8a8a] mt-1">Inferred: <span className="text-[#4a4a4a]">{b.profile.style}</span> · {b.profile.areas.join(", ") || "any area"} · {b.profile.bedrooms[0]}–{b.profile.bedrooms[1]} beds · effective {money(b.profile.effectiveBudget[0])}–{money(b.profile.effectiveBudget[1])}</div>
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="buyer-detail-dialog">
          <DialogTitle className="sr-only">Buyer profile</DialogTitle>
          {open && (
            <div className="space-y-4">
              <div><div className="text-[11px] uppercase tracking-wide text-[#b8935f]">Inferred buyer profile</div><h2 className="text-[22px] font-semibold text-[#2b2b2b]">{open.name}</h2></div>
              <div className="grid grid-cols-2 gap-2 text-[13px]">
                {[["Style", open.profile.style], ["Areas", open.profile.areas.join(", ") || "any"], ["Bedrooms", `${open.profile.bedrooms[0]}–${open.profile.bedrooms[1]}`], ["Effective budget", `${money(open.profile.effectiveBudget[0])}–${money(open.profile.effectiveBudget[1])}`], ["Garden preference", `${Math.round(open.profile.gardenPreference * 100)}%`], ["New-build affinity", `${Math.round(open.profile.newBuildAffinity * 100)}%`]].map(([k, v]) => (
                  <div key={k} className="bg-[#faf7f2] rounded-lg px-3 py-2"><div className="text-[11px] text-[#8a8a8a]">{k}</div><div className="font-medium text-[#2b2b2b]">{v}</div></div>
                ))}
              </div>
              <div>
                <div className="text-[13px] font-medium text-[#2b2b2b] mb-2 flex items-center gap-1.5"><Home size={15} className="text-[#b8935f]" /> Best property matches</div>
                {!matches ? <div className="py-4 grid place-items-center"><Loader2 className="animate-spin text-[#8a8a8a]" size={18} /></div> :
                  <div className="space-y-2">
                    {matches.slice(0, 5).map((m) => (
                      <div key={m.propertyId} className="flex items-center gap-3 border border-[#f0ece5] rounded-lg p-2" data-testid={`buyer-match-${m.propertyId}`}>
                        <span className="text-[15px] font-bold text-[#b8935f] w-12">{m.match}%</span>
                        <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-[#2b2b2b] truncate">{m.title}</div><div className="text-[12px] text-[#8a8a8a]">{money(m.price)} · {m.reasons.slice(0, 2).join(", ")}{m.concern ? ` · ⚠ ${m.concern}` : ""}</div></div>
                      </div>
                    ))}
                  </div>}
              </div>
              <p className="text-[11px] text-[#a8a8a8] italic">Profile affinities and budget stretch are estimates inferred from stated criteria.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBuyerIntelligence;
